/**
 * Markdown-it plugin for processing chart code blocks
 * @fileoverview Converts ```chart blocks with YAML content to dataroom-chart
 * HTML components, wrapped in <div class="cm-chart-widget">. Attribute
 * mapping matches the markdown-editor shared compiler (chart-html.js):
 * type, width, height, orientation, monochrome, color, lineWidth →
 * line-width, radius, minRadius → min-radius, maxRadius → max-radius,
 * labels; data is JSON-stringified as the element's text content.
 * YAML parse errors emit a styled error box instead of a fallback fence.
 */

import yaml from 'js-yaml';

/**
 * Escapes a value for use inside a double-quoted HTML attribute
 * @param {string} value - Raw attribute value
 * @return {string} Escaped attribute value
 */
function escapeAttr(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Escapes HTML special characters in a string
 * @param {string} str - String to escape
 * @return {string} HTML-escaped string
 */
function escapeHtml(str = '') {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Converts YAML chart configuration to a dataroom-chart HTML element
 * @param {string} content - YAML source between the fences
 * @return {string} HTML string for the dataroom-chart element
 * @throws {Error} when the YAML is missing or malformed
 */
function chartContentToHtml(content) {
  const config = yaml.load(content || '');
  if (!config || typeof config !== 'object') {
    throw new Error('Invalid chart configuration');
  }

  const attrs = [];
  if (config.type) attrs.push(`type="${escapeAttr(config.type)}"`);
  if (config.width) attrs.push(`width="${escapeAttr(config.width)}"`);
  if (config.height) attrs.push(`height="${escapeAttr(config.height)}"`);
  if (config.orientation) attrs.push(`orientation="${escapeAttr(config.orientation)}"`);
  if (config.monochrome !== undefined) attrs.push(`monochrome="${escapeAttr(config.monochrome)}"`);
  if (config.color) attrs.push(`color="${escapeAttr(config.color)}"`);
  if (config.lineWidth) attrs.push(`line-width="${escapeAttr(config.lineWidth)}"`);
  if (config.radius) attrs.push(`radius="${escapeAttr(config.radius)}"`);
  if (config.minRadius) attrs.push(`min-radius="${escapeAttr(config.minRadius)}"`);
  if (config.maxRadius) attrs.push(`max-radius="${escapeAttr(config.maxRadius)}"`);
  if (config.labels !== undefined) attrs.push(`labels="${escapeAttr(config.labels)}"`);

  const data = config.data || [];
  return `<dataroom-chart ${attrs.join(' ')}>${JSON.stringify(data)}</dataroom-chart>`;
}

/**
 * Markdown-it plugin for chart blocks
 * @param {Object} md - Markdown-it instance
 */
export default function chartBlocksPlugin(md) {
  const originalFenceRenderer = md.renderer.rules.fence || function(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };

  md.renderer.rules.fence = function(tokens, idx, options, env, self) {
    const token = tokens[idx];
    const info = token.info ? token.info.trim() : '';

    if (info !== 'chart') {
      return originalFenceRenderer(tokens, idx, options, env, self);
    }

    try {
      return `<div class="cm-chart-widget">\n${chartContentToHtml(token.content)}\n</div>\n`;
    } catch (error) {
      return (
        `<div class="chart-error cm-chart-widget-error">\n` +
        `<strong>Chart Error:</strong> ${escapeHtml(error.message)}\n` +
        `<pre><code>${escapeHtml(token.content)}</code></pre>\n</div>\n`
      );
    }
  };
}
